import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import QRCode from "qrcode";
import { Copy, CheckCircle2, ShieldCheck, AlertCircle, CheckCircle } from "lucide-react";
import { authApi } from "@/api/auth";
import { useAppSelector } from "@/store/hooks";

interface TOTPSetupProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

export const TOTPSetup = ({ open, onClose, onComplete }: TOTPSetupProps) => {
  const { userEmail } = useAppSelector((state) => state.auth);
  const [step, setStep] = useState<"qr" | "verify">("qr");
  const [qrCodeUrl, setQrCodeUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const generateTOTP = useCallback(async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await authApi.generateTOTPSecret();

      if ((response.code === 200 || response.success) && response.data) {
        const newSecret = response.data.secret;
        setSecret(newSecret);

        if (response.data.qrCode) {
          const url = await QRCode.toDataURL(response.data.qrCode);
          setQrCodeUrl(url);
        } else {
          const email = userEmail;
          const issuer = "Slike Studio";
          const otpauth = `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${newSecret}&issuer=${encodeURIComponent(issuer)}`;
          const url = await QRCode.toDataURL(otpauth);
          setQrCodeUrl(url);
        }
      } else {
        const errorMsg = response.message || response.msg || "Failed to generate TOTP secret";
        if (errorMsg.toLowerCase().includes("already enabled")) {
          setError("TOTP is already enabled for this user. Please disable it first if you want to set it up again.");
          onClose();
        } else {
          throw new Error(errorMsg);
        }
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Failed to generate QR code. Please try again.";
      if (errorMsg.toLowerCase().includes("already enabled")) {
        setError("TOTP is already enabled for this user. Please disable it first if you want to set it up again.");
        onClose();
      } else {
        setError(errorMsg);
        toast.error(errorMsg);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userEmail, onClose]);

  useEffect(() => {
    if (open && step === "qr") {
      generateTOTP();
      setError("");
      setSuccessMessage("");
      setVerificationCode("");
    }
  }, [open, step, generateTOTP]);

  const copySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiedSecret(true);
    toast.success("Secret key copied to clipboard");
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    setIsLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      const verifyResponse = await authApi.verifyTOTPSetup({
        secret,
        token: verificationCode,
        enable: true
      });

      const isVerified = verifyResponse.code === 200 || verifyResponse.success === true;

      if (!isVerified) {
        throw new Error(verifyResponse.message || verifyResponse.msg || "Invalid verification code");
      }

      setSuccessMessage(verifyResponse.msg || verifyResponse.message || "TOTP verification successful!");

      toast.success("Two-factor authentication enabled successfully!");
      setTimeout(() => {
        completeTOTPSetup();
      }, 500);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : "Verification failed. Please try again.";
      setError(errorMsg);
      setSuccessMessage("");
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const completeTOTPSetup = () => {
    onComplete();
    onClose();
    setStep("qr");
    setVerificationCode("");
    setSecret("");
    setQrCodeUrl("");
    setError("");
    setSuccessMessage("");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-card sm:max-w-md">
        {step === "qr" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                Set Up Two-Factor Authentication
              </DialogTitle>
              <DialogDescription>
                Scan the QR code with your authenticator app
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {isLoading ? (
                <div className="flex justify-center items-center h-48">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : (
                <div className="flex justify-center">
                  {qrCodeUrl && (
                    <div className="p-4 bg-white rounded-lg">
                      <img src={qrCodeUrl} alt="TOTP QR Code" className="w-48 h-48" />
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm bg-destructive/10 p-3 rounded-lg">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  Or enter this key manually:
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={secret}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={copySecret}
                  >
                    {copiedSecret ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-muted/50 p-3 rounded-lg text-sm">
                <p className="font-medium mb-1">Supported Apps:</p>
                <p className="text-muted-foreground">
                  Google Authenticator, Authy, Microsoft Authenticator, 1Password
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button onClick={() => setStep("verify")} className="flex-1 gradient-primary">
                Next
              </Button>
            </div>
          </>
        )}

        {step === "verify" && (
          <>
            <DialogHeader>
              <DialogTitle>Verify Your Setup</DialogTitle>
              <DialogDescription>
                Enter the 6-digit code from your authenticator app
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Verification Code</Label>
                <Input
                  value={verificationCode}
                  onChange={(e) => {
                    setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                    setError("");
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isLoading && verificationCode.length === 6) {
                      e.preventDefault();
                      verifyCode();
                    }
                  }}
                  placeholder="000000"
                  className={`text-center text-2xl tracking-widest font-mono ${error ? "border-destructive" : ""}`}
                  maxLength={6}
                />
              </div>

              {successMessage && (
                <div className="flex items-center gap-2 text-green-600 dark:text-green-400 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded-lg border border-green-200 dark:border-green-800">
                  <CheckCircle className="w-4 h-4" />
                  {successMessage}
                </div>
              )}
              {error && (
                <div className="flex items-center gap-2 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep("qr")} className="flex-1" disabled={isLoading}>
                Back
              </Button>
              <Button onClick={verifyCode} className="flex-1 gradient-primary" disabled={isLoading || verificationCode.length !== 6}>
                {isLoading ? "Verifying..." : "Verify"}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
